import { defineStore } from 'pinia'
import axios from '@axios'
import { useAuthStore } from '@/pinia/auth'

export const useWorkspaceStore = defineStore({
  id: 'workspace',

  state: () => ({
    isOwner: false,
    ownerStatusChecked: false,
    owner: null,
    facilities: [],
    selectedFacility: null,
    loading: false,
    error: null,
    // Team management state
    users: [],
    roles: [],
    usersLoading: false,
    stats: null,
    seatStatus: null
  }),

  getters: {
    hasFacilities: (state) => state.facilities.length > 0,

    facilityCount: (state) => state.facilities.length,

    getFacilityById: (state) => (id) => {
      return state.facilities.find(f => f.id === id)
    },

    // Team getters
    userCount: (state) => state.users.length,

    getUserById: (state) => (id) => {
      return state.users.find(u => u.id === id)
    },

    ownerUsers: (state) => state.users.filter(u => u.is_owner),

    staffUsers: (state) => state.users.filter(u => !u.is_owner),

    isSeatOverflow: (state) => state.seatStatus && state.seatStatus.seat_overflow && state.seatStatus.seat_overflow.is_overflow,

    activeUsers: (state) => state.seatStatus ? state.seatStatus.active_users : 0,

    totalSeats: (state) => state.seatStatus ? state.seatStatus.total_seats : 0,

    baseSeats: (state) => state.seatStatus ? (state.seatStatus.base_seats || 0) : 0,

    addonSeats: (state) => state.seatStatus ? (state.seatStatus.addon_seats || 0) : 0,

    /**
     * Check if at least one facility has core fields filled:
     * name, jenis_faskes, phone, address.
     * Used to gate the Berlangganan (subscription) tab.
     */
    isFacilityComplete: (state) => {
      if (state.facilities.length === 0) return false
      const filled = (v) => typeof v === 'string' && v.trim().length > 0
      return state.facilities.some(f =>
        filled(f.name) && filled(f.jenis_faskes) && filled(f.phone) && filled(f.address)
      )
    }
  },

  actions: {
    /**
     * Check if the current user is an owner
     */
    async checkOwnerStatus() {
      if (this.ownerStatusChecked) {
        return this.isOwner
      }

      try {
        const response = await axios.get('/workspace/check-owner')

        // sendResponse returns {success, data, message}
        const data = response.data.data

        this.isOwner = data.is_owner || false
        this.owner = data.owner || null
        this.ownerStatusChecked = true

        return this.isOwner
      } catch (error) {
        console.error('Failed to check owner status:', error)
        this.isOwner = false
        this.ownerStatusChecked = true
        this.error = error
        return false
      }
    },

    /**
     * Load workspace data (owner + facilities)
     */
    async loadWorkspace() {
      this.loading = true
      this.error = null

      try {
        const response = await axios.get('/workspace')

        // sendResponse returns {success, data, message}
        const data = response.data.data

        this.owner = data.owner
        this.facilities = data.facilities
        this.isOwner = true
        this.ownerStatusChecked = true

        return data
      } catch (error) {
        console.error('Failed to load workspace:', error)
        this.error = error
        throw error
      } finally {
        this.loading = false
      }
    },

    /**
     * Update owner profile
     */
    async updateOwnerProfile(profileData) {
      this.loading = true
      this.error = null

      try {
        // If FormData, use POST; otherwise use PUT
        const isFormData = profileData instanceof FormData
        const method = isFormData ? 'post' : 'put'

        const response = await axios[method]('/workspace/owner/profile', profileData, {
          headers: isFormData ? { 'Content-Type': 'multipart/form-data' } : {}
        })

        // sendResponse returns {success, data, message}
        this.owner = response.data.data

        return response.data
      } catch (error) {
        console.error('Failed to update owner profile:', error)
        this.error = error
        throw error
      } finally {
        this.loading = false
      }
    },

    /**
     * Update facility data
     */
    async updateFacility(facilityId, facilityData) {
      this.loading = true
      this.error = null

      try {
        // If FormData with _method, use POST; otherwise use PUT
        const isFormData = facilityData instanceof FormData
        const method = isFormData ? 'post' : 'put'

        const response = await axios[method](`/workspace/facility/${facilityId}`, facilityData, {
          headers: isFormData ? { 'Content-Type': 'multipart/form-data' } : {}
        })

        // sendResponse returns {success, data, message}
        const data = response.data.data

        // Update facility in the list
        const index = this.facilities.findIndex(f => f.id === facilityId)
        if (index !== -1) {
          this.facilities[index] = data
        }

        // Also update auth store's entity if this is the user's current facility
        const authStore = useAuthStore()
        if (authStore.userData?.entity?.entity?.id === facilityId) {
          authStore.updateEntity(data)
        }

        return response.data
      } catch (error) {
        console.error('Failed to update facility:', error)
        this.error = error
        throw error
      } finally {
        this.loading = false
      }
    },

    /**
     * Switch to a specific facility
     */
    async switchToFacility(facilityId) {
      this.loading = true
      this.error = null

      try {
        const response = await axios.post(`/workspace/facility/${facilityId}/switch`)

        // sendResponse returns {success, data, message}
        const data = response.data.data

        this.selectedFacility = data.entity

        return response.data
      } catch (error) {
        console.error('Failed to switch facility:', error)
        this.error = error
        throw error
      } finally {
        this.loading = false
      }
    },

    /**
     * Reset workspace state
     */
    resetWorkspace() {
      this.isOwner = false
      this.ownerStatusChecked = false
      this.owner = null
      this.facilities = []
      this.selectedFacility = null
      this.loading = false
      this.error = null
      this.users = []
      this.roles = []
      this.usersLoading = false
      this.stats = null
      this.seatStatus = null
    },

    // ==========================================
    // Team Management Actions
    // ==========================================

    /**
     * Load users in the facility
     */
    async loadUsers(params = {}) {
      this.usersLoading = true
      this.error = null

      try {
        const response = await axios.get('/workspace/users', { params })
        this.users = response.data.data
        return this.users
      } catch (error) {
        console.error('Failed to load users:', error)
        this.error = error
        throw error
      } finally {
        this.usersLoading = false
      }
    },

    /**
     * Load available roles
     */
    async loadRoles() {
      try {
        const response = await axios.get('/workspace/roles')
        this.roles = response.data.data
        return this.roles
      } catch (error) {
        console.error('Failed to load roles:', error)
        this.error = error
        throw error
      }
    },

    /**
     * Load workspace statistics
     */
    async loadStats() {
      try {
        const response = await axios.get('/workspace/stats')
        this.stats = response.data.data
        return this.stats
      } catch (error) {
        console.error('Failed to load stats:', error)
        this.error = error
        throw error
      }
    },

    /**
     * Create a new user
     */
    async createUser(userData) {
      this.usersLoading = true
      this.error = null

      try {
        const isFormData = userData instanceof FormData
        const response = await axios.post('/workspace/users', userData, {
          headers: isFormData ? { 'Content-Type': 'multipart/form-data' } : {}
        })
        const newUser = response.data.data

        // Add to users list
        this.users.push(newUser)

        return newUser
      } catch (error) {
        console.error('Failed to create user:', error)
        this.error = error
        throw error
      } finally {
        this.usersLoading = false
      }
    },

    /**
     * Update an existing user
     */
    async updateUser(userId, userData) {
      this.usersLoading = true
      this.error = null

      try {
        const isFormData = userData instanceof FormData
        // For FormData, use POST with _method override since PUT doesn't handle files well
        let response
        if (isFormData) {
          userData.append('_method', 'PUT')
          response = await axios.post(`/workspace/users/${userId}`, userData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          })
        } else {
          response = await axios.put(`/workspace/users/${userId}`, userData)
        }
        const updatedUser = response.data.data

        // Update in users list
        const index = this.users.findIndex(u => u.id === userId)
        if (index !== -1) {
          this.users[index] = updatedUser
        }

        return updatedUser
      } catch (error) {
        console.error('Failed to update user:', error)
        this.error = error
        throw error
      } finally {
        this.usersLoading = false
      }
    },

    /**
     * Delete a user
     */
    async deleteUser(userId) {
      this.usersLoading = true
      this.error = null

      try {
        await axios.delete(`/workspace/users/${userId}`)

        // Remove from users list
        this.users = this.users.filter(u => u.id !== userId)

        return true
      } catch (error) {
        console.error('Failed to delete user:', error)
        this.error = error
        throw error
      } finally {
        this.usersLoading = false
      }
    },

    // ==========================================
    // Seat Management Actions
    // ==========================================

    /**
     * Load seat status for the facility
     */
    async loadSeatStatus() {
      try {
        const response = await axios.get('/workspace/seat-status')
        this.seatStatus = response.data.data
        return this.seatStatus
      } catch (error) {
        console.error('Failed to load seat status:', error)
        this.error = error
        throw error
      }
    },

    /**
     * Deactivate a user
     */
    async deactivateUser(userId) {
      try {
        const response = await axios.post(`/workspace/users/${userId}/deactivate`)
        const result = response.data.data

        // Update in users list
        const index = this.users.findIndex(u => u.id === userId)
        if (index !== -1) {
          this.users[index] = {
            ...this.users[index],
            is_active: false,
            deactivated_at: result.deactivated_at,
          }
        }

        // Refresh seat status
        await this.loadSeatStatus()

        return result
      } catch (error) {
        console.error('Failed to deactivate user:', error)
        this.error = error
        throw error
      }
    },

    /**
     * Activate a previously deactivated user
     */
    async activateUser(userId) {
      try {
        const response = await axios.post(`/workspace/users/${userId}/activate`)
        const result = response.data.data

        // Update in users list
        const index = this.users.findIndex(u => u.id === userId)
        if (index !== -1) {
          this.users[index] = {
            ...this.users[index],
            is_active: true,
            deactivated_at: null,
          }
        }

        // Refresh seat status
        await this.loadSeatStatus()

        return result
      } catch (error) {
        console.error('Failed to activate user:', error)
        this.error = error
        throw error
      }
    },

    // ==========================================
    // Integration Management Actions
    // ==========================================

    /**
     * Load integration credentials for a specific type
     */
    async loadIntegration(type) {
      try {
        const response = await axios.get(`/workspace/integrations/${type}`)
        return response.data.data
      } catch (error) {
        console.error(`Failed to load ${type} integration:`, error)
        this.error = error
        throw error
      }
    },

    /**
     * Save integration credentials
     */
    async saveIntegration(type, data) {
      try {
        const response = await axios.put(`/workspace/integrations/${type}`, data)
        return response.data.data
      } catch (error) {
        console.error(`Failed to save ${type} integration:`, error)
        this.error = error
        throw error
      }
    },

    /**
     * Load M-JKN data (entity-level)
     */
    async loadMjknData() {
      try {
        const response = await axios.get('/workspace/mjkn')
        return response.data.data
      } catch (error) {
        console.error('Failed to load M-JKN data:', error)
        this.error = error
        throw error
      }
    },

    /**
     * Save M-JKN data
     */
    async saveMjknData(data) {
      try {
        const response = await axios.put('/workspace/mjkn', data)
        return response.data.data
      } catch (error) {
        console.error('Failed to save M-JKN data:', error)
        this.error = error
        throw error
      }
    }
  },

  persist: {
    enabled: false // Don't persist workspace data for security
  }
})
